import { getUserToken } from "@/services/storage-data";
import { createLogger } from "@/services/frontdesk/utils/logger";
import config from "@/config";

const logger = createLogger("ChatwootHttpClient");

/**
 * Base HTTP client for Chatwoot API integration.
 *
 * Base URL: Uses MESSAGING_SERVER from config (defaults to http://localhost:8080 for development)
 * Production URL: https://messaging-hub-683372436778.europe-west1.run.app
 * Authentication: GoMake bearer token (same as other FrontDesk services)
 *
 * IMPORTANT: All Chatwoot API calls go through the messaging hub proxy.
 * This enables Gmail label synchronization and other enhanced features.
 * DO NOT connect directly to Chatwoot - always use the messaging hub.
 *
 * Phase 4: Paths stay `/api/accounts/*`. With EMAIL_ENGINE=native on FrontDesk API,
 * those routes are served from Postgres via a Chatwoot-compatible layer (no FE rewrite yet).
 * Domain API `/frontdesk/*` is available for new callers.
 *
 * This client handles authentication, error handling, and response parsing
 * for all Chatwoot API endpoints proxied through the messaging hub.
 */
export class ChatwootHttpClient {
  protected baseUrl: string;

  constructor(baseUrl?: string) {
    // Use provided baseUrl, or config.messaging_server, or fallback to localhost
    // IMPORTANT: We use messaging_server, NOT chatwoot_server, to ensure all calls go through the messaging hub
    const defaultUrl = config.messaging_server || "http://localhost:8080";
    this.baseUrl = (baseUrl || defaultUrl).replace(/\/$/, "");
  }

  /**
   * Make an HTTP request to the Chatwoot API
   * @param endpoint - API endpoint (e.g., "/api/profile")
   * @param options - Fetch options (method, body, headers, etc.)
   * @returns Response data
   */
  protected async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = getUserToken();
    if (!token) {
      throw new Error("No authentication token available");
    }

    const normalizedEndpoint = endpoint.startsWith("/")
      ? endpoint
      : `/${endpoint}`;
    const url = `${this.baseUrl}${normalizedEndpoint}`;

    // Check if body is FormData - if so, don't set Content-Type (browser will set it with boundary)
    const isFormData = options.body instanceof FormData;
    
    // Build headers
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    };
    
    // Only set Content-Type for non-FormData requests
    if (!isFormData) {
      headers["Content-Type"] = "application/json";
    }
    
    // Merge with any custom headers (but filter out empty Content-Type for FormData)
    if (options.headers) {
      const customHeaders = options.headers as Record<string, string>;
      Object.entries(customHeaders).forEach(([key, value]) => {
        // Skip empty Content-Type (used by FormData requests to signal "don't set it")
        if (key.toLowerCase() === 'content-type' && (!value || value === '')) {
          return;
        }
        headers[key] = value;
      });
    }

    let response: Response;
    try {
      response = await fetch(url, {
        ...options,
        headers,
      });
    } catch (error) {
      // Network error (CORS, connection refused, DNS failure, etc.)
      const networkError = new Error(
        `Network error: Unable to connect to messaging hub at ${this.baseUrl}. ` +
        `Please ensure the messaging hub service is running and accessible.`
      );
      logger.error("Network request failed", { url, error });
      throw networkError;
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      // Handle 429 Too Many Requests (Rate Limiting)
      if (response.status === 429) {
        const retryAfter = response.headers.get("Retry-After") || "60";
        const waitTime = parseInt(retryAfter);

        logger.warn(`⚠️ Rate limit exceeded (429 Too Many Requests)`, {
          url: response.url,
          retryAfter: `${waitTime}s`,
          message: "Too many requests. Please slow down API calls.",
          suggestion: "Consider increasing polling intervals or using WebSocket for real-time updates",
        });

        const error = new Error(
          `Rate limit exceeded. The server is receiving too many requests. ` +
          `Please wait ${waitTime} seconds before trying again. ` +
          `Consider using WebSocket connections for real-time updates instead of polling.`
        ) as Error & {
          status?: number;
          errorData?: any;
          retryAfter?: number;
        };
        error.status = 429;
        error.errorData = errorData;
        error.retryAfter = waitTime;
        throw error;
      }

      const nestedMessage =
        errorData.message &&
        typeof errorData.message === "object" &&
        typeof (errorData.message as { message?: unknown }).message === "string"
          ? (errorData.message as { message: string }).message
          : null;

      const diagnosticMessage =
        typeof errorData.diagnostic?.message === "string"
          ? errorData.diagnostic.message
          : typeof errorData.message === "string"
            ? errorData.message
            : nestedMessage;

      const finalMessage =
        diagnosticMessage ||
        errorData.error ||
        (typeof errorData === "string" ? errorData : null) ||
        `HTTP error! status: ${response.status}`;

      logger.error(`API request failed: ${finalMessage}`, {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
        stage: errorData.stage || errorData.diagnostic?.stage,
        correlationId:
          errorData.correlationId || errorData.diagnostic?.correlationId,
      });

      // Create error with additional properties for error handling
      const error = new Error(String(finalMessage)) as Error & {
        status?: number;
        errorData?: any;
        attributes?: string[];
      };
      error.status = response.status;
      error.errorData = errorData;
      error.attributes = errorData.attributes || [];
      throw error;
    }

    // Handle binary responses (like file downloads)
    const contentType = response.headers.get("content-type") || "";
    if (
      contentType.includes("application/octet-stream") ||
      contentType.includes("application/pdf") ||
      contentType.includes("image/") ||
      !contentType.includes("application/json")
    ) {
      return response.blob() as Promise<T>;
    }

    const json = await response.json();

    // Return the JSON response (Chatwoot may have different response format)
    return json as T;
  }

  /**
   * GET request helper
   */
  protected async get<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: "GET" });
  }

  /**
   * POST request helper
   * Handles both JSON and FormData payloads automatically
   */
  protected async post<T>(
    endpoint: string,
    body?: any,
    options: RequestInit = {}
  ): Promise<T> {
    // Check if body is FormData - if so, pass it directly without stringifying
    const isFormData = body instanceof FormData;
    
    return this.request<T>(endpoint, {
      ...options,
      method: "POST",
      body: body ? (isFormData ? body : JSON.stringify(body)) : undefined,
    });
  }

  /**
   * PUT request helper
   * Handles both JSON and FormData payloads automatically
   */
  protected async put<T>(
    endpoint: string,
    body?: any,
    options: RequestInit = {}
  ): Promise<T> {
    // Check if body is FormData - if so, pass it directly without stringifying
    const isFormData = body instanceof FormData;
    
    return this.request<T>(endpoint, {
      ...options,
      method: "PUT",
      body: body ? (isFormData ? body : JSON.stringify(body)) : undefined,
    });
  }

  /**
   * PATCH request helper
   * Handles both JSON and FormData payloads automatically
   */
  protected async patch<T>(
    endpoint: string,
    body?: any,
    options: RequestInit = {}
  ): Promise<T> {
    // Check if body is FormData - if so, pass it directly without stringifying
    const isFormData = body instanceof FormData;
    
    return this.request<T>(endpoint, {
      ...options,
      method: "PATCH",
      body: body ? (isFormData ? body : JSON.stringify(body)) : undefined,
    });
  }

  /**
   * DELETE request helper
   */
  protected async delete<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: "DELETE" });
  }
}

